/**
 * Measurement Input Component
 *
 * Input field for inspection measurements with:
 * - Unit display
 * - Min/max validation indicators
 * - Visual feedback for out-of-range values
 * - Support for common measurement types (mm, %, bar, etc.)
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  TextField,
  Typography,
  InputAdornment,
  Chip,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ErrorIcon from '@mui/icons-material/Error';

interface MeasurementInputProps {
  value: number | null | undefined;
  unit: string;
  onChange?: (value: number | null) => void;
  disabled?: boolean;
  allowClear?: boolean;
  min?: number;
  max?: number;
  warningMin?: number; // Value below which shows warning (but still acceptable)
  label?: string;
  placeholder?: string;
  showProgress?: boolean; // Show visual progress bar for values like tire tread
}

type MeasurementStatus = 'good' | 'warning' | 'critical' | 'unknown';

// Common measurement presets
const measurementPresets: Record<string, { min: number; warningMin: number; max: number }> = {
  mm: { min: 1.6, warningMin: 3, max: 10 }, // Tire tread depth
  '%': { min: 20, warningMin: 30, max: 100 }, // Brake pad percentage
  bar: { min: 1.8, warningMin: 2.0, max: 3.0 }, // Tire pressure
};

export function MeasurementInput({
  value,
  unit,
  onChange,
  disabled = false,
  allowClear = true,
  min,
  max,
  warningMin,
  label,
  placeholder,
  showProgress = true,
}: MeasurementInputProps) {
  const { t } = useTranslation('inspection');
  const [localValue, setLocalValue] = useState<string>(value?.toString() ?? '');

  // Get preset values if available
  const preset = measurementPresets[unit];
  const effectiveMin = min ?? preset?.min;
  const effectiveMax = max ?? preset?.max;
  const effectiveWarningMin = warningMin ?? preset?.warningMin;

  useEffect(() => {
    setLocalValue(value?.toString() ?? '');
  }, [value]);

  const getMeasurementStatus = (): MeasurementStatus => {
    if (value === null || value === undefined) return 'unknown';

    if (effectiveMin !== undefined && value < effectiveMin) {
      return 'critical';
    }
    if (effectiveWarningMin !== undefined && value < effectiveWarningMin) {
      return 'warning';
    }
    return 'good';
  };

  const status = getMeasurementStatus();

  const getStatusColor = () => {
    switch (status) {
      case 'good':
        return 'success.main';
      case 'warning':
        return 'warning.main';
      case 'critical':
        return 'error.main';
      default:
        return 'text.secondary';
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'good':
        return <CheckCircleIcon fontSize="small" color="success" />;
      case 'warning':
        return <WarningIcon fontSize="small" color="warning" />;
      case 'critical':
        return <ErrorIcon fontSize="small" color="error" />;
      default:
        return null;
    }
  };

  const getProgressValue = (): number => {
    if (value === null || value === undefined || effectiveMax === undefined) return 0;
    const minVal = effectiveMin ?? 0;
    const range = effectiveMax - minVal;
    if (range <= 0) return 0;
    return Math.min(100, Math.max(0, ((value - minVal) / range) * 100));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalValue(newValue);
  };

  const handleBlur = () => {
    if (!onChange) return;

    if (localValue === '') {
      if (!allowClear) setLocalValue(value?.toString() ?? '');
      onChange(null);
      return;
    }

    const numValue = parseFloat(localValue);
    if (!isNaN(numValue)) {
      onChange(numValue);
    }
  };

  const getStatusLabel = (): string => {
    switch (status) {
      case 'good': return 'OK';
      case 'warning': return t('measurement.warning');
      case 'critical': return t('measurement.critical');
      default: return '';
    }
  };

  return (
    <Box>
      {label && (
        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
          {label}
        </Typography>
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TextField
          size="small"
          type="number"
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          placeholder={placeholder ?? '0'}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Typography variant="body2" color="text.secondary">
                    {unit}
                  </Typography>
                </InputAdornment>
              ),
            },
            htmlInput: {
              step: unit === 'mm' ? 0.1 : unit === '%' ? 1 : 0.1,
              min: 0,
              style: { width: 80, textAlign: 'right' },
            },
          }}
        />

        {/* Status indicator */}
        {value !== null && value !== undefined && (
          <Tooltip
            title={
              effectiveMin !== undefined
                ? `${t('measurement.minimum', { value: String(effectiveMin), unit })}${effectiveWarningMin !== undefined ? `, ${t('measurement.warningThreshold')}: ${String(effectiveWarningMin)} ${unit}` : ''}`
                : ''
            }
          >
            <Chip
              icon={getStatusIcon() ?? undefined}
              label={getStatusLabel()}
              size="small"
              sx={{
                height: 24,
                backgroundColor:
                  status === 'good'
                    ? 'success.50'
                    : status === 'warning'
                      ? 'warning.50'
                      : status === 'critical'
                        ? 'error.50'
                        : 'action.hover',
                color: getStatusColor(),
                fontWeight: 500,
                '& .MuiChip-icon': {
                  color: 'inherit',
                },
              }}
            />
          </Tooltip>
        )}
      </Box>

      {/* Progress bar for visual representation */}
      {showProgress && effectiveMax !== undefined && value !== null && value !== undefined && (
        <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinearProgress
            variant="determinate"
            value={getProgressValue()}
            sx={{
              flex: 1,
              height: 6,
              borderRadius: 3,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                backgroundColor: getStatusColor(),
                borderRadius: 3,
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" sx={{ minWidth: 50 }}>
            {effectiveMin !== undefined && `${String(effectiveMin)}-${String(effectiveMax)}`}
          </Typography>
        </Box>
      )}

      {/* Range hint */}
      {effectiveMin !== undefined && effectiveMax !== undefined && (
        <Typography variant="caption" color="text.disabled" sx={{ mt: 0.5, display: 'block' }}>
          {t('measurement.acceptable')}: {effectiveMin}-{effectiveMax} {unit}
          {effectiveWarningMin !== undefined && ` (${t('measurement.warningThreshold').toLowerCase()}: ${String(effectiveWarningMin)} ${unit})`}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Compact measurement display for item rows
 */
interface MeasurementBadgeProps {
  value: number | null | undefined;
  unit: string;
  min?: number;
  warningMin?: number;
}

export function MeasurementBadge({ value, unit, min, warningMin }: MeasurementBadgeProps) {
  if (value === null || value === undefined) return null;

  const preset = measurementPresets[unit];
  const effectiveMin = min ?? preset?.min;
  const effectiveWarningMin = warningMin ?? preset?.warningMin;

  let status: MeasurementStatus = 'good';
  if (effectiveMin !== undefined && value < effectiveMin) {
    status = 'critical';
  } else if (effectiveWarningMin !== undefined && value < effectiveWarningMin) {
    status = 'warning';
  }

  const getColor = () => {
    switch (status) {
      case 'good':
        return 'success';
      case 'warning':
        return 'warning';
      case 'critical':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Chip
      label={`${String(value)} ${unit}`}
      size="small"
      color={getColor()}
      variant={status === 'good' ? 'outlined' : 'filled'}
      sx={{ height: 22, fontSize: '0.75rem' }}
    />
  );
}
